import { Link, StyleSheet, Text, View } from '@react-pdf/renderer'
import { FC } from 'react'
import { Asset } from './asset'
import { sanitizePdfText } from './utils/utils'
import { SeverityIcon } from './severityIcon'
import { getIssueTitle, getStatusIcon, getDateString } from '@ui/utils/utils'

const MAX_WIDTH = 320
const MAX_ASSETS_TO_SHOW = 10
const MAX_EMAIL_RECIPIENTS_TO_SHOW = 10

export const Issue: FC<{ issue: TIssue }> = ({ issue }) => {
    const { status, severity, name, assets } = issue
    const hasMoreAssets = (assets?.length ?? 0) > MAX_ASSETS_TO_SHOW
    const statusIcon = getStatusIcon(severity, status)
    const issueName = getIssueTitle(status, name)

    const renderIssue = (issue: TGeneralIssue) => {
        const { long_description, ports, resolution_url, assets } = issue

        return (
            <>
                <SeverityIcon severity={statusIcon} />
                <View style={{ marginBottom: 16 }}>
                    <Text style={styles.name}>{issueName}</Text>
                    <Text style={styles.description}>
                        {sanitizePdfText(long_description)}
                    </Text>
                    {ports && ports.length > 0 && (
                        <View style={styles.portsContainer}>
                            <Text style={styles.portsName}>
                                {ports?.length > 1 ? 'Ports' : 'Port'}:
                            </Text>
                            <Text style={styles.portsValue}>
                                {`${ports?.join(', ')}`}
                            </Text>
                        </View>
                    )}
                    {resolution_url && (
                        <Text style={styles.resolutionInstruction}>
                            <Link src={resolution_url}>
                                Resolution instructions
                            </Link>
                        </Text>
                    )}
                    {assets && assets.length > 0 && (
                        <View style={styles.assets}>
                            <Text style={styles.assetsTitle}>
                                Associated assets:
                            </Text>
                            {assets
                                .slice(0, MAX_ASSETS_TO_SHOW)
                                .map((asset) => (
                                    <Asset
                                        key={asset.id}
                                        asset={asset as TAsset}
                                    />
                                ))}
                            {hasMoreAssets && (
                                <Text>{`and ${assets.length - MAX_ASSETS_TO_SHOW} more`}</Text>
                            )}
                        </View>
                    )}
                </View>
            </>
        )
    }

    const renderFraudDefenseIssue = (issue: TFraudDefenseIssue) => {
        const { resolution_url, email_data } = issue

        return (
            <>
                <SeverityIcon severity={statusIcon} />
                <View style={{ marginBottom: 16 }}>
                    <Text style={styles.name}>{issueName}</Text>
                    <Text style={styles.description}>
                        At-Bay detected an incoming email that poses a risk to
                        your organization.
                    </Text>
                    <Text>
                        <Text style={styles.textBold}>Subject:</Text>{' '}
                        {email_data?.subject}
                    </Text>
                    <Text>
                        <Text style={styles.textBold}>Date:</Text>{' '}
                        {getDateString(email_data?.date_sent)}
                    </Text>
                    <Text>
                        <Text style={styles.textBold}>From:</Text>{' '}
                        {email_data?.sender.email_address}
                    </Text>

                    {resolution_url && (
                        <Text style={styles.resolutionInstruction}>
                            <Link src={resolution_url}>
                                Resolution instructions
                            </Link>
                        </Text>
                    )}
                    {email_data.to_recipients.length > 0 && (
                        <View style={styles.assets}>
                            <Text style={styles.assetsTitle}>
                                Associated assets:
                            </Text>
                            <View>
                                <Text>
                                    {email_data.to_recipients
                                        .slice(0, MAX_EMAIL_RECIPIENTS_TO_SHOW)
                                        .map(
                                            (recipient) =>
                                                recipient.email_address
                                        )
                                        .join(', ')}
                                    {email_data?.to_recipients?.length >
                                        MAX_EMAIL_RECIPIENTS_TO_SHOW && (
                                        <Text style={styles.textBold}>{`  +${
                                            email_data.to_recipients.length -
                                            MAX_EMAIL_RECIPIENTS_TO_SHOW
                                        }`}</Text>
                                    )}
                                </Text>
                            </View>
                        </View>
                    )}
                </View>
            </>
        )
    }

    return (
        <View>
            <View style={styles.separator} />
            <View style={styles.issueContainer} wrap={false}>
                {issue.issue_type === 'FRAUD_DEFENSE'
                    ? renderFraudDefenseIssue(issue as TFraudDefenseIssue)
                    : renderIssue(issue as TGeneralIssue)}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    issueContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        fontSize: 8.7,
    },
    name: {
        maxWidth: MAX_WIDTH,
        fontWeight: 'bold',
    },
    assetsTitle: {
        fontWeight: 'bold',
    },
    description: {
        maxWidth: MAX_WIDTH,
    },
    portsContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        gap: 4,
    },
    portsName: {
        fontWeight: 'bold',
    },
    portsValue: {
        maxWidth: MAX_WIDTH - 20,
    },
    resolutionInstruction: {
        marginTop: 10,
        maxWidth: MAX_WIDTH,
    },
    assets: {
        backgroundColor: '#F2F2F2',
        width: '100%',
        fontSize: 6.75,
        marginTop: 10,
        padding: 8,
    },
    separator: {
        marginBottom: 16,
        backgroundColor: '#F2F2F2',
        width: '95%',
        height: 1,
    },
    textBold: {
        fontWeight: '400',
    },
})
